import React, { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Spinner } from "@/components/ui/spinner";

type ImgProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  containerClassName?: string;
  spinnerClassName?: string;
  showSpinnerBg?: boolean;
};

/**
 * ImageWithLoader
 * - Shows a spinner overlay until the image is fully loaded (handles cached images too)
 * - Fades image in when ready
 */
export function ImageWithLoader({
  src,
  alt,
  className,
  containerClassName,
  spinnerClassName,
  showSpinnerBg = true,
  onLoad,
  onError,
  ...rest
}: ImgProps) {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  // Reset flags when src changes
  useEffect(() => {
    setLoaded(false);
    setErrored(false);
  }, [src]);

  // If the image is cached, it may already be complete by the first paint
  useEffect(() => {
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [src]);

  const handleLoad: React.ReactEventHandler<HTMLImageElement> = (e) => {
    setLoaded(true);
    onLoad?.(e);
  };

  const handleError: React.ReactEventHandler<HTMLImageElement> = (e) => {
    setErrored(true);
    onError?.(e);
  };

  return (
    <div className={cn("relative", containerClassName)}>
      {!loaded && !errored && (
        <div
          className={cn(
            "absolute inset-0 flex items-center justify-center",
            showSpinnerBg && "bg-black/5"
          )}
        >
          <Spinner className={cn("h-6 w-6 text-gray-500", spinnerClassName)} />
        </div>
      )}
      {/* Render the image with a fade-in */}
      {/* Ensure it occupies the container area by default */}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        onLoad={handleLoad}
        onError={handleError}
        className={cn(
          "w-full h-full object-contain transition-opacity duration-300",
          loaded ? "opacity-100" : "opacity-0",
          className
        )}
        {...rest}
      />
    </div>
  );
}
